<template>
  <div class="container">
    <transition name="fade">
      <div class="column-center" v-if="show">
        <div class="column-title">
          Update Your Profile
        </div>
        <div class="column-body">
          <div class="subcolumn flex">
            <transition name="fade">
              <div class="section-body">
                <div class="section-subtitle">
                  First Name
                </div>
                <div
                  :class="[{ hidden: !isEditingFirstName }, 'char-limit-row']"
                >
                  Max {{ this.firstNameCharLimit }} characters
                </div>
                <div class="section-row" v-show="!isEditingFirstName">
                  <span class="name-item">
                    {{ profileInfo.firstName }}
                  </span>
                  <span class="edit-icon-container">
                    <font-awesome-icon
                      :icon="['fas', 'edit']"
                      class="edit-icon"
                      @click="enableFirstNameEditing"
                    />
                  </span>
                </div>
                <div class="section-row" v-show="isEditingFirstName">
                  <input
                    ref="firstNameInput"
                    class="profile-input"
                    v-model="newFirstName"
                    type="text"
                    @input="clipInputString"
                    :maxlength="firstNameCharLimit"
                    placeholder="Update your first name"
                    autocomplete="off"
                    autocorrect="off"
                    autofocus
                    spellcheck="false"
                    @keydown.enter.prevent="saveFirstName"
                  >
                  <span :class="[{ 'disabled': !newFirstName }, 'button']"  @click="saveFirstName">
                    Save
                  </span>
                  <span class="button cancel" @click="cancelFirstName">
                    Cancel
                  </span>
                </div>
              </div>
            </transition>
            <transition name="fade">
              <div class="section-body">
                <div class="section-subtitle">
                  Company Name
                </div>
                <div
                  :class="[{ hidden: !isEditingCompanyName }, 'char-limit-row']"
                >
                  Max {{ this.companyNameCharLimit }} characters
                </div>
                <div class="section-row" v-show="!isEditingCompanyName">
                  <span class="name-item">
                    {{ profileInfo.companyName }}
                  </span>
                  <span class="edit-icon-container">
                    <font-awesome-icon
                      :icon="['fas', 'edit']"
                      class="edit-icon"
                      @click="enableCompanyNameEditing"
                    />
                  </span>
                </div>
                <div class="section-row" v-show="isEditingCompanyName">
                  <input
                    ref="companyNameInput"
                    class="profile-input"
                    v-model="newCompanyName"
                    type="text"
                    @input="clipInputString"
                    :maxlength="companyNameCharLimit"
                    placeholder="Update your company name"
                    autocomplete="off"
                    autocorrect="off"
                    autofocus
                    spellcheck="false"
                    @keydown.enter.prevent="saveCompanyName"
                  >
                  <span :class="[{ 'disabled': !newCompanyName }, 'button']"  @click="saveCompanyName">
                    Save
                  </span>
                  <span class="button cancel" @click="cancelCompanyName">
                    Cancel
                  </span>
                </div>
              </div>
            </transition>
          </div>
          <div class="subcolumn stretch-height">
            <transition name="fade">
              <div class="section-body picture">
                <div class="profile-picture-container">
                  <div class="profile-picture">
                    <img
                      ref="profileImage"
                      class="lazyload"
                      :src="defaultProfilePicture"
                      :data-src="profileImageUrl"
                    >
                  </div>
                </div>
                <div
                  v-show="!showImageActionButtons"
                  class="button-row"
                >
                  <input
                    type="file"
                    id="profile-picture-input"
                    @change="uploadImage"
                    accept="image/jpeg, image/png, image/gif"
                  >
                  <label
                    for="profile-picture-input"
                    class="button wide"
                  >
                    Update your photo
                  </label>
                </div>
                <div
                  v-show="showImageActionButtons"
                  class="button-row"
                >
                  <SpinnerButton
                    class="button"
                    :isProcessing="isSavingImage"
                    @click="saveImage"
                  >
                    Save
                  </SpinnerButton>
                  <SpinnerButton
                    class="button cancel"
                    :isDisabled="isSavingImage"
                    @click="clearImage"
                  >
                    Cancel
                  </SpinnerButton>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </transition>
    <div class="column-center spinner-top-padded" v-if="!show">
      <Spinner color="teal" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Spinner from '@/components/utilComponents/Spinner';
import SpinnerButton from '@/components/utilComponents/SpinnerButton.vue';
const defaultProfilePicture = require('@/assets/default-profile-picture.jpg');
import compressImage from '@/utils/compressImage';

export default Vue.extend({
  name: 'ProfileView',
  components: {
    Spinner,
    SpinnerButton
  },
  data: () => ({
    show: false,
    profileInfo: {},
    newFirstName: '',
    newCompanyName: '',
    isEditingFirstName: false,
    isEditingCompanyName: false,
    isSavingImage: false,
    firstNameCharLimit: 16,
    companyNameCharLimit: 48,
    fileToUpload: null,
    showImageActionButtons: false,
    profileImageUrl: '',
    defaultProfilePicture
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    }
  },
  methods: {
    clipInputString() {
      if (this.maxLength && this.value.length > this.maxLength) {
        this.value = this.value.slice(0, this.maxLength);
      }
    },
    async getProfileInfo() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getprofileinfoforuser`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
      const profileInfo = response.data.profileInfo;
      if (profileInfo.firstName) {
        this.newFirstName = profileInfo.firstName;
      }
      if (profileInfo.companyName) {
        this.newCompanyName = profileInfo.companyName;
      }
      if (profileInfo.profileImageUrl) {
        this.profileImageUrl = profileInfo.profileImageUrl;
      }
      this.profileInfo = profileInfo;
      this.show = true;
    },
    async enableFirstNameEditing() {
      this.cancelCompanyName()
      this.isEditingFirstName = true;
      this.$nextTick(() => {
        this.$refs.firstNameInput.focus();
      });
    },
    async saveFirstName() {
      if (!this.newFirstName) {
        return;
      }
      this.isEditingFirstName = false;
      this.profileInfo.firstName = this.newFirstName;
      await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/updatefirstname`, {
        auth0UserId: this.auth0UserId,
        firstName: this.newFirstName
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
    },
    async cancelFirstName() {
      this.isEditingFirstName = false;
      this.newFirstName = this.profileInfo.firstName;
    },
    async enableCompanyNameEditing() {
      this.cancelFirstName();
      this.isEditingCompanyName = true;
      this.$nextTick(() => {
        this.$refs.companyNameInput.focus();
      });
    },
    async saveCompanyName() {
      if (!this.newCompanyName) {
        return;
      }
      this.isEditingCompanyName = false;
      this.profileInfo.companyName = this.newCompanyName;

      await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/updatecompanyname`, {
        auth0UserId: this.auth0UserId,
        companyName: this.newCompanyName
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });
    },
    async cancelCompanyName() {
      this.isEditingCompanyName = false;
      this.newCompanyName = this.profileInfo.companyName;
    },
    async uploadImage(event) {
      const {file, src} = await compressImage(event.target.files[0]);
      this.fileToUpload = file;
      this.$refs.profileImage.src = src;
      this.showImageActionButtons = true;
    },
    async saveImage() {
      try {
        this.isSavingImage = true;
        if (this.fileToUpload) {
          const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getprofilepictureuploadlink`, {
            auth0UserId: this.auth0UserId,
            imageType: this.fileToUpload.type
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          });
          // NOTE: Careful, use axios.put here. DO NOT USE axios.post because you won't match the signature!
          await this.axios.put(
            response.data.uploadLink,
            this.fileToUpload,
            { 
              headers: {
                'Content-Type': this.fileToUpload.type
              }
            }
          );
        }
        this.isSavingImage = false;
      } catch (error) {
        this.isSavingImage = false;
        console.log('Error:', error);
      }
      this.showImageActionButtons = false;
    },
    clearImage() {
      this.$refs.profileImage.src = this.profileInfo.profileImageUrl;
      this.showImageActionButtons = false;
    }
  },
  created: async function () {
    try {
      await this.getProfileInfo();
    } catch (error) {
      console.log('Error:', error);
    }
  },
})
</script>

<style scoped lang="scss">
.container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  color: $dark-brown;
  min-height: min-content;
  padding-top: 6em;
  margin: 0 10em;
}

.column-title {
  font-size: 1.4em;
  font-weight: bold;
  padding: 0.4em 0.8em;
}

.column-body {
  display: flex;
}

.subcolumn {
  min-width: max-content;
  & + .subcolumn {
    margin-left: 1.6em; 
  }
  &.flex {
    flex: 1;
  }
  &.stretch-height {
    min-height: 100%;
  }
}

.section {
  width: 100%;
  min-height: min-content;
  padding: 0.4em 0.8em;
}

.section-title {
  font-size: 1.4em;
  font-weight: bold;
  padding: 0.4em 0.8em;
}

.section-body {
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 0.1em solid $dark-sand;
  border-radius: 0.2em;
  padding: 0.6em 0.8em;
  &.picture {
    min-height: 100%;
    box-sizing:border-box;
  }
  & + .section-body {
    margin-top: 1.4em;
  }
}

.section-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 3em;
  width: 28em;
}

.profile-picture-container {
  width: 14em;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  align-self: center;
  margin-bottom: 1.4em;
}

.profile-picture {
  height: 10em;
  width: 10em;
  border-radius: 50%;
  overflow: hidden;
  & > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
}

.name-item {
  width: max-content;
  padding: 0.4em 0.8em;
  margin: 0.6em 0.6em 0.6em 0.1em;
  font-size: 1.1em;
}

.profile-input {
  flex: 1;
  min-width: 15em;
  padding: 0.4em 0.8em;
  margin: 0.6em 0.6em 0.6em 0;
  color: $dark-brown;
  font-family: inherit;
  font-size: 1.1em;
}

#profile-picture-input {
  position: absolute;
  opacity: 0;
  z-index: -1;
}

.button-row {
  display: flex;
  justify-content: center;
}

.button {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.2em;
  width: max-content;
  margin: 0 0.2em;
  color: $natural-white;
  background-color: $brand-teal;
  text-align: center;
  border-radius: 0.2em;
  box-shadow: 0 1px 1px 1px $light-gray;
  user-select: none;
  cursor: pointer;
  &:hover {
    background-color: $light-teal;
  }
  &.wide {
    width: 100%;
  }
  &.cancel {
    background-color: $crimson;
  }
  &.cancel:hover {
    background-color: $light-crimson;
  }
}

.edit-icon-container {
  display: flex;
  flex: 1;
  font-size: 1.4em;
  margin: auto 0.6em;
}

.edit-icon {
  color: $brand-teal;
  cursor: pointer;
  &:hover {
    color: $light-teal;
  }
}

.char-limit-row {
  font-size: 0.8em;
  margin-left: 0.6em;
  color: $light-smoke-gray;
  &.hidden {
    opacity: 0;
  }
}

.disabled {
  background-color: $light-gray!important;
  pointer-events: none;
}
</style>
